import { buildRequestConfig } from '@/common/request/request-builder';
import { useUiStore } from '@/modules/ui/store';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { handleRequestError } from './error-handler';
import { ApiResponse, isMutationMethod, isResolvedResponse, Method, RequestParams } from './types';

let _instance: AxiosInstance = null;

const _request = async <TData = any>(method: Method, url: string, params?: RequestParams, files?: RequestParams) => {
    const config = await buildRequestConfig(method, url, params, files);
    return getInstance().request<ApiResponse<TData>>(config) as unknown as ApiResponse<TData>;
};

const getInstance = (): AxiosInstance => {
    if (_instance == null) {
        _instance = axios.create({
            baseURL: process.env.VUE_APP_API_URL
        });

        _instance.interceptors.request.use(request => {
            if (isMutationMethod(request.method)) {
                useUiStore().beginLoading();
            }

            return request;
        });

        _instance.interceptors.response.use(handleResponse, handleResponseError);
    }

    return _instance;
};

const handleResponse = async (axiosResponse: AxiosResponse<ApiResponse>) => {
    if (isMutationMethod(axiosResponse.config.method)) {
        useUiStore().endLoading();
    }

    const internalResponse = axiosResponse.data;
    if (isResolvedResponse(internalResponse.code)) {
        return internalResponse;
    }

    await handleRequestError(internalResponse);
    return Promise.reject(internalResponse);
};

const handleResponseError = async (error: AxiosError<ApiResponse>) => {
    if (isMutationMethod(error.config.method)) {
        useUiStore().endLoading();
    }

    const errResponse = error.response.data;
    await handleRequestError(errResponse);

    return Promise.reject(errResponse);
};

const connection = {
    doGet: (url: string, params?: RequestParams) => _request(Method.GET, url, params),
    doPut: (url: string, params?: RequestParams, files?: RequestParams) => _request(Method.PUT, url, params, files),
    doPost: (url: string, params?: RequestParams, files?: RequestParams) => _request(Method.POST, url, params, files),
    doDelete: (url: string, params?: RequestParams) => _request(Method.DELETE, url, params)
};

export default connection;
